import { ChangesetDTO, ChangesetOpDTO, ChangesetStates, PageBaseDTO, SpacePageDTO } from "../models/ChangeRequestDTO";

/**
 * A change request as a reviewer reads it: one item per page it touches (its ops merged), placed in the space tree, with what an
 * approve would run into (pages edited since the proposal). The ops stay the source of truth; this is only how they are shown.
 */

export type ReviewKind = "created" | "updated" | "deleted" | "moved" | "files";

/** What happened to an item's ops once the change request was applied (absent while it is open, rejected or withdrawn). */
export type ReviewOutcome = "applied" | "failed" | "skipped";

export interface ReviewItem {
    /** `page:<id>` for an existing page, `new:<tempId>` for a page the change request creates. */
    key: string;
    pageId?: number;
    kind: ReviewKind;
    /** The title after the change. */
    title: string;
    /** The title before (the base's, else the page's now), when the change request renames the page. */
    previousTitle?: string;
    /** Indexes into the changeset's ops, in order. */
    opIndexes: number[];
    /** The proposed body (`null`: the body is cleared); absent when the body does not change. */
    proposedContent?: string | null;
    proposedRules?: Record<string, unknown>;
    /** The rules before the change: the base's when the change request kept one, else the page's now. */
    previousRules?: Record<string, unknown> | null;
    /** The page as the change request found it, when the server kept it: the "before" of the author's own changes. */
    base?: PageBaseDTO;
    /** Where a reorder puts the page. */
    move?: { parentKey: string; parentTitle: string; index?: number };
    uploads: string[];
    /** Existing pages under a deleted one (they go with it). */
    childCount: number;
    /** The page changed since the change request read it: an approve fails on this item. */
    stale: boolean;
    /** The page the ops name is gone from the space. */
    missing: boolean;
    modifiedAt?: string | null;
    modifiedBy?: string | null;
    outcome?: ReviewOutcome;
    error?: string;
}

export interface ReviewTreeNode {
    key: string;
    title: string;
    /** Absent on context nodes: ancestors shown only to place the changed pages. */
    item?: ReviewItem;
    children: ReviewTreeNode[];
}

export interface ReviewSummary {
    created: number;
    updated: number;
    deleted: number;
    moved: number;
    files: number;
}

export interface Review {
    items: ReviewItem[];
    /** Items in tree order (depth first): the order the reviewer walks them. */
    ordered: ReviewItem[];
    tree: ReviewTreeNode[];
    summary: ReviewSummary;
    stale: ReviewItem[];
}

export const RootKey = "root";

/** Same comparison as Ark.Core (`PageDigests.EtagMatches`): quotes and `W/` ignored, case-insensitive. */
export function etagMatches(expected: string | null | undefined, actual: string | null | undefined): boolean {
    if (!expected?.trim() || !actual?.trim()) return false;
    const clean = (s: string) => s.trim().replace(/^"|"$/g, "").replace(/W\//gi, "").replace(/^"|"$/g, "").toLowerCase();
    return clean(expected) === clean(actual);
}

function refKey(ref: string | number | null | undefined): string {
    if (ref === undefined || ref === null || ref === "") return RootKey;
    const s = String(ref);
    return s.startsWith("new:") ? s : `page:${s}`;
}

function opKey(op: ChangesetOpDTO): string | undefined {
    switch (op.op) {
        case "create":
            return op.tempId ? `new:${op.tempId}` : undefined;
        case "upload":
            return op.pageRef !== undefined && op.pageRef !== null ? `page:${op.pageRef}` : undefined;
        default:
            return op.id !== undefined && op.id !== null ? `page:${op.id}` : undefined;
    }
}

function kindOf(item: ReviewItem, ops: ChangesetOpDTO[]): ReviewKind {
    const names = item.opIndexes.map((i) => ops[i].op);
    if (names.includes("delete")) return "deleted";
    if (names.includes("create")) return "created";
    if (item.proposedContent !== undefined || item.proposedRules || item.previousTitle !== undefined) return "updated";
    if (item.move) return "moved";
    return item.uploads.length ? "files" : "updated";
}

const isReviewed = (state: string) =>
    state === ChangesetStates.Applied || state === ChangesetStates.Approved || state === ChangesetStates.Failed;

export function buildReview(changeset: ChangesetDTO, pages: SpacePageDTO[], currentEtags: Record<number, string> = {}): Review {
    const ops = changeset.ops ?? [];
    const byId = new Map(pages.map((p) => [p.id, p]));
    const bases = new Map((changeset.bases ?? []).map((b) => [b.pageId, b]));
    const items = new Map<string, ReviewItem>();
    const isOpen = changeset.state === ChangesetStates.Proposed;

    const titleOf = (key: string): string => {
        if (key === RootKey) return "Space root";
        const item = items.get(key);
        if (item) return item.title;
        if (key.startsWith("new:")) return `New page ${key.substring(4)}`;
        const id = Number(key.substring(5));
        return byId.get(id)?.title ?? `Page #${id}`;
    };

    ops.forEach((op, index) => {
        const key = opKey(op);
        if (!key) return;
        let item = items.get(key);
        if (!item) {
            const pageId = key.startsWith("page:") ? Number(key.substring(5)) : undefined;
            const page = pageId !== undefined ? byId.get(pageId) : undefined;
            const base = pageId !== undefined ? bases.get(pageId) : undefined;
            item = {
                key,
                pageId,
                kind: "updated",
                title: op.op === "create" ? op.title ?? "(untitled)" : page?.title ?? base?.title ?? op.title ?? `Page #${pageId}`,
                opIndexes: [],
                uploads: [],
                childCount: pageId !== undefined ? pages.filter((p) => p.parentId === pageId).length : 0,
                stale: false,
                missing: pageId !== undefined && pages.length > 0 && !page,
                previousRules: base ? base.rules ?? null : page?.rules ?? null,
                base,
                modifiedAt: page?.modifiedAt,
                modifiedBy: page?.modifiedBy,
            };
            items.set(key, item);
        }
        item.opIndexes.push(index);

        if (op.op === "create") {
            item.proposedContent = op.content ?? "";
            if (op.rules) item.proposedRules = op.rules;
        } else if (op.op === "update") {
            if (op.content !== undefined && op.content !== null) item.proposedContent = op.content;
            else if (op.clearContent) item.proposedContent = null;
            if (op.rules) item.proposedRules = op.rules;
            // a rename is judged against the title the author saw (the base's), not one someone gave the page since
            const before = item.previousTitle ?? item.base?.title ?? item.title;
            if (op.title !== undefined && op.title !== null && op.title !== before) {
                item.previousTitle = before;
                item.title = op.title;
            }
        } else if (op.op === "reorder") {
            item.move = { parentKey: refKey(op.parentRef), parentTitle: "", index: op.index ?? undefined };
        } else if (op.op === "upload" && op.name) {
            item.uploads.push(op.name);
        }

        // the server checks every op's ifMatch against the page as it is when the change request is approved
        if (isOpen && item.pageId !== undefined && op.ifMatch) {
            const current = currentEtags[item.pageId] ?? byId.get(item.pageId)?.etag;
            if (current && !etagMatches(op.ifMatch, current)) item.stale = true;
        }
    });

    const list = Array.from(items.values());
    for (const item of list) {
        item.kind = kindOf(item, ops);
        if (item.move) item.move.parentTitle = titleOf(item.move.parentKey);
        if (item.missing && isOpen) item.stale = true;
        if (isReviewed(changeset.state)) {
            const results = item.opIndexes.map((i) => changeset.results?.find((r) => r.index === i));
            const failed = results.find((r) => r?.status === "failed");
            if (failed) {
                item.outcome = "failed";
                item.error = failed.error;
            } else if (results.some((r) => !r)) {
                item.outcome = "skipped";
            } else {
                item.outcome = "applied";
            }
        }
    }

    // the tree: each item under its (new) parent, with the existing ancestors as context
    const nodes = new Map<string, ReviewTreeNode>();
    const roots: ReviewTreeNode[] = [];
    const parentOf = (key: string): string => {
        const item = items.get(key);
        if (item?.move) return item.move.parentKey;
        if (key.startsWith("new:")) {
            const create = ops.find((o) => o.op === "create" && `new:${o.tempId}` === key);
            return refKey(create?.parentRef);
        }
        const parentId = byId.get(Number(key.substring(5)))?.parentId;
        return parentId === undefined || parentId === null ? RootKey : `page:${parentId}`;
    };
    const nodeFor = (key: string, seen: Set<string> = new Set()): ReviewTreeNode => {
        const existing = nodes.get(key);
        if (existing) return existing;
        const node: ReviewTreeNode = { key, title: titleOf(key), item: items.get(key), children: [] };
        nodes.set(key, node);
        seen.add(key);
        const parent = parentOf(key);
        if (parent === RootKey || seen.has(parent)) roots.push(node);
        else nodeFor(parent, seen).children.push(node);
        return node;
    };
    list.forEach((item) => nodeFor(item.key));

    const order = (node: ReviewTreeNode) => {
        const id = node.key.startsWith("page:") ? Number(node.key.substring(5)) : undefined;
        return id !== undefined ? byId.get(id)?.orderIndex ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
    };
    const sort = (level: ReviewTreeNode[]) => {
        level.sort((a, b) => order(a) - order(b));
        level.forEach((n) => sort(n.children));
    };
    sort(roots);

    const ordered: ReviewItem[] = [];
    const walk = (level: ReviewTreeNode[]) =>
        level.forEach((n) => {
            if (n.item) ordered.push(n.item);
            walk(n.children);
        });
    walk(roots);

    const count = (kind: ReviewKind) => list.filter((i) => i.kind === kind).length;
    return {
        items: list,
        ordered,
        tree: roots,
        summary: {
            created: count("created"),
            updated: count("updated"),
            deleted: count("deleted"),
            moved: count("moved"),
            files: list.reduce((n, i) => n + i.uploads.length, 0),
        },
        stale: list.filter((i) => i.stale),
    };
}

/** The ids whose current body the review needs: existing pages whose body changes or that are deleted. */
export function pagesToDiff(review: Review): number[] {
    return review.items
        .filter((i) => i.pageId !== undefined && !i.missing && (i.proposedContent !== undefined || i.kind === "deleted"))
        .map((i) => i.pageId as number);
}
